import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

interface LocationState {
  filePath?: string;
  fileName?: string;
  pdfUrl?: string;
  invoiceId?: string;
}

const InvoiceSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const userId = getAuth().currentUser?.uid;

  // URL 파라미터에서 데이터 추출
  const searchParams = new URLSearchParams(location.search);
  const filePath = searchParams.get('filePath') || undefined;
  const fileName = searchParams.get('fileName') || undefined;
  const pdfUrl = searchParams.get('pdfUrl') || undefined;
  const invoiceId = searchParams.get('invoiceId') || undefined;

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const result = t(key, options);
      return typeof result === 'string' ? result : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  useEffect(() => {
    console.log("InvoiceSuccessPage mounted/params changed:");
    console.log("filePath:", filePath);
    console.log("fileName:", fileName);
    console.log("pdfUrl:", pdfUrl);
    console.log("invoiceId:", invoiceId);

    if (userId && fileName) {
      // 여기서 트래킹 로직을 추가할 수 있습니다
      console.log('Invoice tracking for user:', userId, 'invoice:', invoiceId);
    }
  }, [filePath, fileName, pdfUrl, userId, invoiceId]);

  const handleViewPDF = async () => {
    try {
      setLoading(true);
      
      if (pdfUrl) {
        // PDF URL이 있으면 새 탭에서 열기
        window.open(pdfUrl, '_blank');
      } else if (filePath) {
        // 로컬 파일 경로가 있는 경우 (웹에서는 제한적)
        console.log('Local file path:', filePath);
        alert(safeT('invoiceSuccess.localFileNotSupported'));
      } else {
        throw new Error(safeT('invoiceSuccess.noFileToOpen') || 'No file available to open.');
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      alert(
        safeT('invoiceSuccess.error') + ': ' + 
        (error instanceof Error ? error.message : String(error))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      setLoading(true);
      
      if (pdfUrl) {
        // Web Share API 사용
        if (navigator.share) {
          await navigator.share({
            title: safeT('invoiceSuccess.shareTitle', { fileName: fileName || 'Invoice' }),
            text: safeT('invoiceSuccess.shareMessage', { fileName: fileName || 'Invoice' }),
            url: pdfUrl
          });
        } else {
          // Fallback: 클립보드에 URL 복사
          await navigator.clipboard.writeText(pdfUrl);
          alert(safeT('invoiceSuccess.urlCopied'));
        }
      } else if (filePath) {
        // 로컬 파일의 경우
        console.log('Local file path for sharing:', filePath);
        alert(safeT('invoiceSuccess.localFileNotSupported'));
      } else {
        throw new Error(safeT('invoiceSuccess.noFileToShare') || 'No file available for sharing.');
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      alert(
        safeT('invoiceSuccess.error') + ': ' + 
        (error instanceof Error ? error.message : String(error))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleGoToInvoices = () => {
    navigate('/invoices');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {safeT('invoiceSuccess.title')}
            </h1>
            
            <p className="text-gray-600">
              {safeT('invoiceSuccess.message')}
            </p>
          </div>

          {/* File Name Display */}
          {fileName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center">
                <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">{fileName}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleViewPDF}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {safeT('invoiceSuccess.loading')}
                </div>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {safeT('invoiceSuccess.openPdf')}
                </>
              )}
            </button>

            <button
              onClick={handleShare}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {safeT('invoiceSuccess.loading')}
                </div>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  {safeT('invoiceSuccess.share')}
                </>
              )}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoToInvoices}
                className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {safeT('invoiceSuccess.viewInvoices')}
              </button>

              <button
                onClick={handleGoHome}
                className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {safeT('invoiceSuccess.goHome')}
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              {safeT('invoiceSuccess.additionalInfo')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSuccessPage;







